import type { FieldFormatter, Function } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo } from 'react';

import { findField } from '@/components/ui';
import { toFileId, toFunctionId, useGetFile, useGetFunction } from '@/hooks/api';
import { useModelConfigStore } from '@/store';
import { FieldContextType, FieldStateValueType, FormatterFnType, importJS } from '@/utils';

type FormatterResolved = FieldFormatter & {
  function?: Function;
  transform?: FormatterFnType;
};

export type UseFormatterOpts = {
  fieldId: string;
  formatterId: string;
};

export const useFormatter = ({ fieldId, formatterId }: UseFormatterOpts) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const { data: formatterFn } = useGetFunction(
    toFunctionId(field?.formatters?.find((f) => f.id === formatterId)?.functionId)
  );
  const { data: formatterBlob } = useGetFile(toFileId(formatterFn?.fileUrl));

  const formatter = useMemo(async () => {
    if (!field?.formatters) return;
    const f = field.formatters.find((f) => f.id === formatterId);
    if (!f) return;
    const fmt: FormatterResolved = { ...f };

    if (fmt && formatterBlob) {
      fmt.transform = (await importJS(formatterBlob)).default;
    }

    return fmt;
  }, [field?.formatters, formatterId, formatterBlob]);

  const run = useCallback(
    async (state: FieldStateValueType, context: FieldContextType): Promise<FieldStateValueType> => {
      const fmt = await formatter;
      if (fmt && fmt.transform) {
        const formatterContext = structuredClone(context);
        formatterContext.config = structuredClone(fmt.config);
        const { result } = fmt.transform({ event: structuredClone(state), context: formatterContext });

        if (result) {
          state = result;
        }
      }

      return state;
    },
    [formatter]
  );

  return { run };
};
